import { useCallback, useMemo, useState } from 'react';
import { Plus, X, ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react';

/**
 * JsonSchemaForm — renders an HTML form from a JSON Schema object.
 *
 * Supports the subset the bundled + community connection adapters actually
 * use (kept narrow on purpose so the renderer stays small):
 *
 *   - ``type: string``             text input
 *   - ``type: string`` + ``enum``  select dropdown
 *   - ``type: string`` + ``format: uri``     URL input with basic validation
 *   - ``type: string`` + ``format: credential``  vault picker (looks up
 *                                              values from the supplied
 *                                              ``vault`` prop)
 *   - ``type: number``/``integer`` numeric input (with min/max/step)
 *   - ``type: boolean``            toggle switch
 *   - ``type: array`` of ``{type: string}``  chip-style input (add / remove)
 *   - ``type: object`` with ``properties``  recursive fieldset
 *
 * Anything else renders as a JSON textarea fallback. This is deliberate
 * — a hard dependency on a full JSON Schema renderer would be overkill for
 * the bounded schema subset we control.
 *
 * Props:
 *   - ``schema``     the JSON Schema (object with ``properties``)
 *   - ``value``      the current value (object)
 *   - ``onChange``   ``(newValue) => void``
 *   - ``vault``      ``{ [key: string]: '•' | undefined }`` — for the
 *                    ``format: credential`` widget; values are masked so
 *                    the form never sees real secrets
 *   - ``depth``      internal — start at 0
 */
export default function JsonSchemaForm({ schema, value = {}, onChange, vault, depth = 0 }) {
  if (!schema || typeof schema !== 'object') return null;
  const props = schema.properties || {};
  const required = new Set(schema.required || []);
  return (
    <div className={`jsf-root jsf-depth-${Math.min(depth, 4)}`}>
      {Object.entries(props).map(([key, prop]) => (
        <Field
          key={key}
          name={key}
          schema={prop}
          value={value?.[key]}
          required={required.has(key)}
          onChange={(v) => onChange({ ...value, [key]: v })}
          vault={vault}
          depth={depth}
        />
      ))}
    </div>
  );
}

function Field({ name, schema, value, required, onChange, vault, depth }) {
  const label = schema.title || name;
  const description = schema.description;

  const handle = useCallback((v) => onChange(v), [onChange]);

  let widget;
  if (schema.enum && Array.isArray(schema.enum) && schema.enum.length > 0) {
    widget = (
      <select
        className="form-input"
        value={value ?? ''}
        onChange={(e) => handle(e.target.value)}
      >
        <option value="">— select —</option>
        {schema.enum.map((opt) => (
          <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
        ))}
      </select>
    );
  } else if (schema.type === 'boolean') {
    widget = (
      <label className="jsf-toggle">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => handle(e.target.checked)}
        />
        <span>{value ? 'On' : 'Off'}</span>
      </label>
    );
  } else if (schema.type === 'integer' || schema.type === 'number') {
    widget = (
      <input
        type="number"
        className="form-input"
        value={value ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') return handle(undefined);
          const n = schema.type === 'integer' ? parseInt(raw, 10) : parseFloat(raw);
          handle(Number.isFinite(n) ? n : raw);
        }}
        min={schema.minimum}
        max={schema.maximum}
        step={schema.type === 'integer' ? 1 : 'any'}
      />
    );
  } else if (schema.type === 'array' && schema.items?.type === 'string') {
    widget = <ChipInput value={Array.isArray(value) ? value : []} onChange={handle} />;
  } else if (schema.type === 'object' && schema.properties) {
    widget = (
      <JsonSchemaForm
        schema={schema}
        value={value || {}}
        onChange={handle}
        vault={vault}
        depth={depth + 1}
      />
    );
  } else if (schema.type === 'string' && schema.format === 'credential') {
    widget = <CredentialInput name={name} value={value} vault={vault} onChange={handle} />;
  } else if (schema.type === 'string' && schema.format === 'uri') {
    widget = (
      <input
        type="url"
        className="form-input"
        value={value ?? ''}
        onChange={(e) => handle(e.target.value)}
        placeholder={schema.examples?.[0] || 'https://'}
      />
    );
  } else if (schema.type === 'string') {
    widget = (
      <input
        type="text"
        className="form-input"
        value={value ?? ''}
        onChange={(e) => handle(e.target.value)}
      />
    );
  } else {
    // Fallback: render raw JSON so unknown constructs are still editable.
    widget = (
      <textarea
        rows={4}
        className="form-input"
        value={value === undefined ? '' : JSON.stringify(value, null, 2)}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw.trim() === '') return handle(undefined);
          try {
            handle(JSON.parse(raw));
          } catch {
            // ignore parse error — user is mid-typing
          }
        }}
      />
    );
  }

  return (
    <div className="jsf-field">
      <label className="jsf-label">
        {label}
        {required && <span className="jsf-required">*</span>}
      </label>
      {description && <div className="jsf-description">{description}</div>}
      {widget}
    </div>
  );
}

function ChipInput({ value, onChange }) {
  const [draft, setDraft] = useState('');
  const items = Array.isArray(value) ? value : [];
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) { setDraft(''); return; }
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="jsf-chip-input">
      <div className="jsf-chips">
        {items.map((item, idx) => (
          <span key={idx} className="jsf-chip">
            {item}
            <button type="button" onClick={() => remove(idx)} aria-label="Remove">
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <div className="jsf-chip-add">
        <input
          type="text"
          className="form-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add(); }
          }}
          placeholder="add and press Enter"
        />
        <button type="button" className="btn btn-sm" onClick={add}>
          <Plus size={12} />
        </button>
      </div>
    </div>
  );
}

function CredentialInput({ name, value, vault, onChange }) {
  const [revealed, setRevealed] = useState(false);
  // Vault values come pre-masked as bullets; we never want to expose them
  // back to the form even by accident. Treat any all-bullet string as
  // "already stored" — editing it means overwriting with a new value.
  const masked = vault && Object.prototype.hasOwnProperty.call(vault, name) &&
    vault[name] && /^•+$/.test(vault[name]);
  const displayValue = revealed
    ? (value || '')
    : (masked && !value ? '••••••••' : (value || ''));
  return (
    <div className="jsf-credential">
      <input
        type={revealed ? 'text' : 'password'}
        className="form-input"
        value={displayValue}
        onChange={(e) => onChange(e.target.value)}
        placeholder={masked ? 'Saved (type to replace)' : 'paste secret here'}
        autoComplete="off"
        spellCheck={false}
      />
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => setRevealed(!revealed)}
        title={revealed ? 'Hide' : 'Show'}
      >
        {revealed ? <EyeOff size={12} /> : <Eye size={12} />}
      </button>
      {masked && (
        <span className="jsf-stored-indicator" title="A value is already saved in the vault">
          ● stored
        </span>
      )}
    </div>
  );
}
